import { useSelector } from 'react-redux';

import type { OfferLabelProps } from 'src/components/atoms/OfferLabel/OfferLabel';
import type { ProductBadgeType } from 'src/components/atoms/ProductBadge/ProductBadge';
import { getProductBadge } from 'src/components/layouts/PDP/getProductBadge';
import type { ProductPriceProps } from 'src/components/molecules/ProductPrice/ProductPrice';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { selectOptions } from 'src/redux/basket/selectors';
import { selectOfferLabelContent } from 'src/redux/content/selectors';
import { selectProduct } from 'src/redux/product/selectors';
import { getOptionsCostRange } from 'src/redux/product/utils/getOptionsCostRange';
import { selectReviewsAverageRating, selectReviewsTotalCount } from 'src/redux/reviews/selectors';

/**
 * View model for the product summary: everything PDPLayoutView renders about the
 * product itself, above and around the CTA.
 *
 * Price and offer label stay in one hook because they are coupled: the price is
 * a minimum when the product's options add cost, and the sale percentage is
 * reddened only when the offer label says so.
 */
export interface ProductSummaryViewModel {
  badge?: ProductBadgeType;
  description: string;
  /** null = offer label hidden */
  offerLabel: Pick<OfferLabelProps, 'countdown' | 'message' | 'promoCode' | 'showInRed'> | null;
  price: {
    preSalePrices: ProductPriceProps['preSalePrices'];
    price: ProductPriceProps['price'];
    priceIsMinimum: boolean;
    salePercentage: number;
    salePercentageInRed: boolean;
  };
  productReviews: {
    count: number;
    /** Fragment link to the reviews section */
    href: string;
    rating: number | null;
  };
  title: string;
}

export const useProductSummaryViewModel = (): ProductSummaryViewModel => {
  const product = useSelector(selectProduct);
  const options = useSelector(selectOptions);
  const reviewsTotalCount = useSelector(selectReviewsTotalCount);
  const reviewsAverageRating = useSelector(selectReviewsAverageRating);
  const offerLabelContent = useSelector(selectOfferLabelContent);

  const optionsCostRange = getOptionsCostRange(options, product.price.currency);
  const priceIsMinimum = optionsCostRange.maxOptional + optionsCostRange.maxRequired > 0;

  const offerLabelAllowedForProduct =
    !offerLabelContent?.showOnlyForDiscountedProducts || product.onSale;
  const offerLabel =
    offerLabelContent && offerLabelAllowedForProduct
      ? {
          countdown: offerLabelContent.countdown,
          message: offerLabelContent.message,
          promoCode: offerLabelContent.promoCode,
          showInRed: offerLabelContent.showInRed,
        }
      : null;

  return {
    badge: getProductBadge(product),
    description: product.description,
    offerLabel,
    price: {
      preSalePrices: product.preSalePrices,
      price: product.price,
      priceIsMinimum,
      salePercentage: product.salePercentage || 0,
      salePercentageInRed: Boolean(offerLabel?.showInRed),
    },
    /*
      Shared with the "View all" button, the reviews summary and the drawer, so all say
      the same numbers. Both settle after hydration, so neither is in the server markup;
      the `aggregateRating` structured data deliberately stays on UPS, which exists during SSR.
    */
    productReviews: {
      count: reviewsTotalCount,
      href: `#${REVIEWS_URL_FRAGMENT_ID}`,
      rating: reviewsAverageRating,
    },
    title: product.title,
  };
};
